import { styled, Typography } from '@mui/material';

import { BackIcon } from '../../../icons';
import { IconButton } from '../../atoms/IconButton';
import { APP_SHELL_TOP_OFFSET } from '../AppShell/appShellLayout';
import { type PageLayoutProps, usePageLayout } from './usePageLayout';

type Props = PageLayoutProps;

type OwnerState = {
  fixedHeader: boolean;
};

const PageRoot = styled('div')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  ...(ownerState.fixedHeader && {
    minHeight: 0,
    [theme.breakpoints.up('sm')]: {
      // Inside an `AppShell`, the top bar shown below `md` is subtracted too.
      height: `calc(100vh - ${APP_SHELL_TOP_OFFSET} - ${theme.spacing(4)})`,
    },
  }),
}));

const PageHeader = styled('header')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  flexShrink: 0,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const HeaderMain = styled('div')({
  flex: 1,
  minWidth: 0,
  width: '100%',
});

const TitleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const BackSlot = styled('div')(({ theme }) => ({
  flexShrink: 0,
  marginLeft: theme.spacing(-1),
}));

const TitleGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  minWidth: 0,
  overflowWrap: 'anywhere',
  textWrap: 'balance',
  fontSize: theme.typography.h3.fontSize,
  [theme.breakpoints.up('sm')]: {
    fontSize: theme.typography.h1.fontSize,
  },
}));

const Status = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

const Subtitle = styled(Typography)({
  overflowWrap: 'anywhere',
});

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  flexShrink: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    justifyContent: 'flex-end',
  },
}));

const PageBody = styled('div')<{ ownerState: OwnerState }>(({ ownerState }) => ({
  minWidth: 0,
  ...(ownerState.fixedHeader && {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  }),
}));

export function PageLayout(props: Props) {
  const hook = usePageLayout(props);
  const ownerState = { fixedHeader: hook.fixedHeader };

  return (
    <PageRoot ownerState={ownerState}>
      <PageHeader>
        <HeaderMain>
          {hook.showSubtitleTop ? <Subtitle variant="subtitle1">{props.subtitle}</Subtitle> : null}

          <TitleRow>
            {props.backHref ? (
              <BackSlot>
                <IconButton
                  href={props.backHref}
                  onClick={props.onBackClick}
                  tooltip={hook.backLabel}
                >
                  <BackIcon />
                </IconButton>
              </BackSlot>
            ) : null}

            <TitleGroup>
              <PageTitle variant="h1">{props.title}</PageTitle>
              {props.status ? <Status>{props.status}</Status> : null}
            </TitleGroup>
          </TitleRow>

          {hook.showSubtitleBottom ? <Subtitle variant="subtitle1">{props.subtitle}</Subtitle> : null}
        </HeaderMain>

        {props.action ? <Actions>{props.action}</Actions> : null}
      </PageHeader>

      <PageBody ownerState={ownerState}>{props.children}</PageBody>
    </PageRoot>
  );
}

export type { Props as PageLayoutProps };
